import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { getDeviceId } from '../lib/deviceId';

type InviteStatus = 'loading' | 'valid' | 'expired' | 'accepted' | 'invalid';

interface InvitePreview {
  status: 'valid' | 'expired' | 'accepted' | 'invalid';
  firstName?: string;
  email?: string;
}

export function AcceptInvite() {
  const [params, setParams] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [inviteStatus, setInviteStatus] = useState<InviteStatus>('loading');
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setInviteStatus('invalid');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<InvitePreview>(`/auth/invite-preview?token=${encodeURIComponent(token)}`);
        if (cancelled) return;
        setPreview(data);
        setInviteStatus(data.status);
      } catch {
        if (!cancelled) setInviteStatus('invalid');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post('/auth/accept-invite', {
        token,
        password,
        phone: phone || undefined,
        deviceId: getDeviceId(),
      });
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not accept the invitation.');
    } finally {
      setSubmitting(false);
    }
  }

  async function onResendInvitation() {
    setResending(true);
    setError(null);
    setResendMessage(null);
    try {
      const result = await api.post<{ acceptUrl: string; emailSent: boolean; firstName: string }>(
        '/auth/resend-invite',
        { token },
      );
      const newToken = new URL(result.acceptUrl, window.location.origin).searchParams.get('token');
      if (newToken) {
        setParams({ token: newToken });
        setInviteStatus('valid');
        setPreview({ status: 'valid', firstName: result.firstName });
      }
      setResendMessage(
        result.emailSent
          ? `A fresh invitation link was sent${preview?.email ? ` to ${preview.email}` : ''}. You can set your password below.`
          : 'A new invitation link is ready — set your password below.',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not resend the invitation.');
    } finally {
      setResending(false);
    }
  }

  if (!token || inviteStatus === 'invalid') {
    return (
      <div className="mx-auto max-w-sm text-center">
        <h1 className="mb-2 font-display text-2xl text-cream-50">Invitation required</h1>
        <p className="text-cream-300/70">This page needs a valid invitation link.</p>
      </div>
    );
  }

  if (inviteStatus === 'loading') {
    return (
      <div className="mx-auto max-w-sm text-center">
        <p className="text-cream-300/70">Checking your invitation…</p>
      </div>
    );
  }

  if (inviteStatus === 'accepted') {
    return (
      <div className="mx-auto max-w-sm text-center">
        <h1 className="mb-2 font-display text-2xl text-cream-50">Already joined</h1>
        <p className="text-cream-300/70">This invitation has already been accepted.</p>
        <Link to="/login" className="btn-primary mt-6 inline-block">
          Sign in
        </Link>
      </div>
    );
  }

  if (inviteStatus === 'expired') {
    return (
      <div className="mx-auto max-w-sm">
        <h1 className="mb-2 text-center font-display text-2xl text-cream-50">Invitation expired</h1>
        <p className="text-center text-sm text-cream-300/70">
          {preview?.firstName ? `Hi ${preview.firstName}, ` : ''}
          this invitation link is no longer valid. We can send you a fresh one.
        </p>
        {error && (
          <p className="mt-4 rounded border border-wine-500/50 bg-wine-900/40 px-3 py-2 text-sm text-wine-100">
            {error}
          </p>
        )}
        {resendMessage && (
          <p className="mt-4 rounded border border-gold-600/40 bg-ink-900/60 px-3 py-2 text-sm text-gold-200">
            {resendMessage}
          </p>
        )}
        <button
          type="button"
          className="btn-primary mt-6 w-full"
          disabled={resending}
          onClick={onResendInvitation}
        >
          {resending ? 'Sending…' : 'Resend invitation'}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm">
      <h1 className="mb-2 text-center font-display text-3xl text-cream-50">Accept your invitation</h1>
      {preview?.firstName && (
        <p className="mb-6 text-center text-sm text-cream-300/70">Welcome, {preview.firstName}.</p>
      )}
      {resendMessage && (
        <p className="mb-4 rounded border border-gold-600/40 bg-ink-900/60 px-3 py-2 text-sm text-gold-200">
          {resendMessage}
        </p>
      )}
      <form onSubmit={onSubmit} className="card space-y-4">
        {error && (
          <p className="rounded border border-wine-500/50 bg-wine-900/40 px-3 py-2 text-sm text-wine-100">
            {error}
          </p>
        )}
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
            Choose a password
          </label>
          <input
            type="password"
            className="field"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={10}
            required
          />
          <p className="mt-1 text-xs text-cream-300/40">At least 10 characters.</p>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">
            Phone (optional)
          </label>
          <input
            type="tel"
            className="field"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
          />
        </div>
        <button type="submit" className="btn-primary w-full" disabled={submitting}>
          {submitting ? 'Setting up…' : 'Join the Club'}
        </button>
      </form>
    </div>
  );
}
